import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { getInitials } from '../utils/format';
import TermsGate from './TermsGate';
import Icon from './Icon';
import './Layout.css';

function Layout() {
  const { user, signOut } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [notifCount, setNotifCount] = useState(0);

  useEffect(() => {
    const loadCounts = async () => {
      try {
        const [unread, pending, notifs] = await Promise.all([
          api.getUnreadCount(),
          api.getPendingConnections(),
          api.getNotificationUnreadCount(),
        ]);
        setUnreadCount(unread.unreadCount);
        setPendingCount(pending.length);
        setNotifCount(notifs.unreadCount);
      } catch (err) {
        console.error('Failed to load counts:', err);
      }
    };

    loadCounts();
    const interval = setInterval(loadCounts, 30000);
    return () => clearInterval(interval);
  }, []);


  const navClass = ({ isActive }) => `nav-link ${isActive ? 'active' : ''}`;
  const mobileClass = ({ isActive }) => `nav-mobile-link ${isActive ? 'active' : ''}`;
  const avatar = (
    <div className="avatar avatar-sm">
      {user?.photo_url ? (
        <img src={user.photo_url} alt="" />
      ) : (
        getInitials(user?.name || 'U')
      )}
    </div>
  );

  return (
    <div className="layout">
      <header className="header">
        <div className="header-content container">
          <NavLink to="/" className="logo" aria-label="around home">
            around
          </NavLink>

          <nav className="nav-desktop" aria-label="Main">
            <NavLink to="/" className={navClass} end>Home</NavLink>
            <NavLink to="/search" className={navClass}>Search</NavLink>
            <NavLink to="/marketplace" className={navClass}>Tee Times</NavLink>
            <NavLink to="/connections" className={navClass}>
              Connections
              {pendingCount > 0 && <span className="badge-count">{pendingCount}</span>}
            </NavLink>
            <NavLink to="/messages" className={navClass}>
              Messages
              {unreadCount > 0 && <span className="badge-count">{unreadCount}</span>}
            </NavLink>
          </nav>

          <div className="header-actions">
            <NavLink to="/search" className={({ isActive }) => `header-icon-btn header-search-mobile ${isActive ? 'active' : ''}`} aria-label="Search golfers">
              <Icon name="search" size={20} />
            </NavLink>
            <NavLink
              to="/notifications"
              className={({ isActive }) => `header-icon-btn ${isActive ? 'active' : ''}`}
              aria-label={notifCount > 0 ? `Notifications, ${notifCount} unread` : 'Notifications'}
            >
              <Icon name="bell" size={20} />
              {notifCount > 0 && <span className="badge-dot">{notifCount > 9 ? '9+' : notifCount}</span>}
            </NavLink>
            <NavLink to="/qr" className={({ isActive }) => `nav-qr-btn ${isActive ? 'active' : ''}`}>
              <Icon name="qr" size={17} /> My QR
            </NavLink>
            <NavLink to="/profile" className="profile-link" aria-label="Your profile">
              {avatar}
              <span className="profile-name">{user?.name?.split(' ')[0]}</span>
            </NavLink>
            <button onClick={signOut} className="header-signout" aria-label="Sign out" title="Sign out">
              <Icon name="signOut" size={19} />
            </button>
          </div>
        </div>
      </header>

      <nav className="nav-mobile" aria-label="Main">
        <NavLink to="/" className={mobileClass} end>
          <Icon name="home" size={23} />
          <span>Home</span>
        </NavLink>
        <NavLink to="/marketplace" className={mobileClass}>
          <Icon name="calendar" size={23} />
          <span>Tee Times</span>
        </NavLink>
        <NavLink to="/qr" className={({ isActive }) => `nav-mobile-qr ${isActive ? 'active' : ''}`} aria-label="My QR code">
          <span className="nav-mobile-qr-tile"><Icon name="qr" size={26} /></span>
        </NavLink>
        <NavLink to="/connections" className={mobileClass}>
          <Icon name="people" size={23} />
          <span>People</span>
          {pendingCount > 0 && <span className="badge-count-mobile">{pendingCount}</span>}
        </NavLink>
        <NavLink to="/messages" className={mobileClass}>
          <Icon name="chat" size={23} />
          <span>Messages</span>
          {unreadCount > 0 && <span className="badge-count-mobile">{unreadCount}</span>}
        </NavLink>
      </nav>

      <main className="main-content">
        <Outlet />
      </main>

      <footer className="app-footer">
        <span className="app-footer-mark">never lose a golf buddy</span>
        <span>&copy; {new Date().getFullYear()} around</span>
        <Link to="/terms">Terms</Link>
        <Link to="/privacy">Privacy</Link>
      </footer>

      <TermsGate />
    </div>
  );
}

export default Layout;
